// Server-side TTS via ElevenLabs (proxied through /api/tts).
// Full-buffer strategy: fetch the entire PCM response before pushing to Simli,
// so the avatar audio buffer never underruns mid-sentence (which causes the
// video to stutter / hold a frame). For 1 to 3 sentence replies this adds
// roughly 0.5 to 2 s before speaking starts but eliminates jitter.

import type { SimliAvatarClient } from "./simli";

interface SpeakOptions {
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (err: string) => void;
}

const CHUNK_BYTES = 6000;
const BYTES_PER_SECOND = 32000;

export class TTS {
  private simli: SimliAvatarClient | null = null;
  private currentAbort: AbortController | null = null;

  setSimli(client: SimliAvatarClient | null): void {
    this.simli = client;
  }

  async speak(text: string, opts: SpeakOptions = {}): Promise<void> {
    this.cancel();
    if (!this.simli || !this.simli.isReady()) {
      opts.onError?.("avatar_not_ready");
      return;
    }
    const ac = new AbortController();
    this.currentAbort = ac;

    try {
      const res = await fetch("/api/tts?format=pcm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
        signal: ac.signal,
      });
      if (!res.ok) {
        throw new Error(`tts_${res.status}`);
      }
      const buf = new Uint8Array(await res.arrayBuffer());
      if (ac.signal.aborted) return;
      if (buf.length === 0) {
        opts.onEnd?.();
        return;
      }

      opts.onStart?.();

      let i = 0;
      while (i + CHUNK_BYTES <= buf.length) {
        this.simli.sendAudio(buf.subarray(i, i + CHUNK_BYTES));
        i += CHUNK_BYTES;
      }
      if (i < buf.length) {
        const pad = new Uint8Array(CHUNK_BYTES);
        pad.set(buf.subarray(i), 0);
        this.simli.sendAudio(pad);
      }

      const durationMs = Math.ceil((buf.length / BYTES_PER_SECOND) * 1000);
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, durationMs);
        ac.signal.addEventListener("abort", () => {
          clearTimeout(timer);
          resolve();
        });
      });
      if (ac.signal.aborted) return;
      opts.onEnd?.();
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      opts.onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      if (this.currentAbort === ac) this.currentAbort = null;
    }
  }

  cancel(): void {
    this.currentAbort?.abort();
    this.currentAbort = null;
    this.simli?.clearBuffer();
  }

  get isSpeaking(): boolean {
    return this.currentAbort !== null;
  }

  static isSupported(): boolean {
    return true;
  }
}
